// Developer drop-in component to see current icon list
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import * as Icons from "@qubit/autoparts";
import { useMemo } from "react";

type IconComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>;

type Group = "20px" | "24px" | "30x20" | "other";
const groupOrder: readonly Group[] = [
  "20px",
  "24px",
  "30x20",
  "other"
] as const;

function isIconComponent(value: unknown): value is IconComponent {
  // Check if it's a function (function component)
  if (typeof value === "function") {
    return true;
  }

  // Check if it's a forwardRef object with render function
  if (typeof value === "object" && value !== null) {
    const obj = value as Record<string, unknown>;
    // React.forwardRef creates objects with $$typeof and render properties
    return (
      obj.$$typeof === Symbol.for("react.forward_ref") ||
      typeof obj.render === "function"
    );
  }

  return false;
}

function isIconName(name: string): boolean {
  // Icon names typically end with size indicators or are specific icon names
  return (
    name.endsWith("20Px") ||
    name.endsWith("24Px") ||
    name.endsWith("Icon") ||
    name.includes("Icon") || // Include things like PickStatusIcon, BarcodeIcon, etc.
    ["AccessPoint", "FlexCellX", "FlexCellXY", "FlexCellY"].includes(name)
  );
}

function groupOf(name: string): Group {
  if (name.endsWith("20Px")) return "20px";
  if (name.endsWith("24Px")) return "24px";
  // Your 30x20 set uses names like AccessPoint / FlexCellX / FlexCellXY / FlexCellY
  return ["AccessPoint", "FlexCellX", "FlexCellXY", "FlexCellY"].includes(name)
    ? "30x20"
    : "other";
}

function dimsFor(group: Group): { width?: number; height?: number } {
  switch (group) {
    case "20px":
      return { width: 20, height: 20 };
    case "24px":
      return { width: 24, height: 24 };
    case "30x20":
      return { width: 30, height: 20 };
    default:
      return { width: 24, height: 24 };
  }
}

export default function IconTable(): JSX.Element {
  const rows = useMemo(() => {
    const iconsRecord = Icons as Record<string, unknown>;
    const keys = Object.keys(iconsRecord);

    const collected = keys
      .filter(isIconName) // Filter by name pattern first
      .map((key) => {
        const value = iconsRecord[key];
        if (!isIconComponent(value)) return null;
        const name = String(key);
        const group = groupOf(name);
        return { name, group, Component: value };
      })
      .filter(
        (v): v is { name: string; group: Group; Component: IconComponent } =>
          v !== null
      )
      .sort((a, b) => {
        const byGroup =
          groupOrder.indexOf(a.group) - groupOrder.indexOf(b.group);
        return byGroup !== 0 ? byGroup : a.name.localeCompare(b.name);
      });

    return collected;
  }, []);

  return (
    <Box sx={{ p: 2, minWidth: 0, maxWidth: "100%" }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        @qubit/autoparts — {rows.length} icon exports
      </Typography>

      <TableContainer
        component={Paper}
        sx={{ maxWidth: "100%", overflowX: "auto" }}
      >
        <Table
          size="small"
          aria-label="icons table"
          sx={{ tableLayout: "fixed", width: "100%" }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 88 }}>Preview</TableCell>
              <TableCell>Name</TableCell>
              <TableCell sx={{ width: 112 }}>Group</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(({ name, group, Component }) => {
              const dims = dimsFor(group);

              // Extra safety check to ensure Component is renderable
              if (!Component || typeof Component !== "function") {
                return null;
              }

              return (
                <TableRow key={name} hover>
                  <TableCell>
                    {(() => {
                      try {
                        // Create base props
                        const baseProps = {
                          ...dims,
                          "aria-label": name
                        };

                        // Add specific props based on icon type
                        let props: Record<string, unknown> = { ...baseProps };

                        if (name === "PickStatusIcon") {
                          props = {
                            ...props,
                            status: "completed",
                            exception: undefined
                          };
                        } else if (name === "BarcodeIcon") {
                          // BarcodeIcon has specific defaults: height=40, width=52
                          props = {
                            height: 40,
                            width: 52,
                            barcodeColor: "black",
                            disabled: false,
                            hide: false,
                            "aria-label": name
                          };
                        } else if (name.includes("Status")) {
                          props = { ...props, status: "active" };
                        } else if (name.includes("Pick")) {
                          props = { ...props, status: "active" };
                        }

                        return <Component {...props} />;
                      } catch (error) {
                        console.warn(`Failed to render icon ${name}:`, error);
                        return (
                          <span style={{ color: "red", fontSize: "12px" }}>
                            Error: {name}
                          </span>
                        );
                      }
                    })()}
                  </TableCell>
                  <TableCell
                    sx={{
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                      verticalAlign: "top"
                    }}
                  >
                    <code>{name}</code>
                  </TableCell>
                  <TableCell>{group}</TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>No icon exports detected.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
